import type { ContactCenterBrowserRegistrationChangedPayload } from '../realtime/contact-center-realtime-types';
import { VoiceRequestRejectedError, voiceApi } from './api';
import {
  getBrowserActivationId,
  getBrowserDeviceInstanceId,
  getBrowserTabId,
  replaceRetiredBrowserDeviceInstanceId,
} from './browser-device-instance';
import type {
  VoiceBrowserInstallationEnsure,
  VoiceBrowserInstallationPreparing,
  VoiceBrowserInstallationProjection,
  VoiceBrowserInstallationStatus,
} from './types';

export const browserInstallationLimitCode = 'VOICE_BROWSER_INSTALLATION_LIMIT';
export const browserActivationCollisionCode = 'VOICE_BROWSER_ACTIVATION_COLLISION';
export const browserPreparationInProgressCode = 'VOICE_BROWSER_PREPARATION_IN_PROGRESS';
export const browserDeviceIdentityRetiredCode = 'VOICE_BROWSER_DEVICE_IDENTITY_RETIRED';

export type BrowserRegistrationTarget = {
  installationId: string;
  generation: number;
  sessionId: string;
};

export type BrowserRegistrationAuthorityPath =
  | 'provider_event'
  | 'verified_hint'
  | 'provider_snapshot'
  | 'recovery_read';

export type RegistrationObservation = BrowserRegistrationTarget & {
  status: VoiceBrowserInstallationStatus;
  stateVersion: number;
  authorityPath: BrowserRegistrationAuthorityPath;
  registeredAt: string | null;
  occurredAt: string | null;
};

type Waiter = {
  target: BrowserRegistrationTarget;
  resolve: (value: RegistrationObservation) => void;
  reject: (error: Error) => void;
  timeout: number;
};

type CoordinatorDependencies = {
  readInstallation: (installationId: string) => Promise<VoiceBrowserInstallationProjection>;
  timeoutMs?: number;
};

const terminalStatuses = new Set<VoiceBrowserInstallationStatus>([
  'revoked',
  'expired',
  'failed',
]);

function targetKey(target: BrowserRegistrationTarget): string {
  return JSON.stringify([target.installationId, target.generation, target.sessionId]);
}

function observationFromRealtime(
  payload: ContactCenterBrowserRegistrationChangedPayload,
): RegistrationObservation {
  const authorityPath: BrowserRegistrationAuthorityPath =
    payload.observationSource === 'handle_hint_verified'
      ? 'verified_hint'
      : payload.observationSource === 'ami_event'
        ? 'provider_event'
        : payload.observationSource === 'snapshot'
          ? 'provider_snapshot'
          : 'provider_event';
  return {
    installationId: payload.installationId,
    generation: payload.generation,
    sessionId: payload.sessionId,
    status: payload.status,
    stateVersion: payload.stateVersion,
    authorityPath,
    registeredAt: payload.registeredAt,
    occurredAt: payload.occurredAt,
  };
}

function observationFromProjection(
  projection: VoiceBrowserInstallationProjection,
  target: BrowserRegistrationTarget,
): RegistrationObservation {
  const active = {
    generation: projection.generation,
    sessionId: projection.sessionId,
    status: projection.status,
  };
  const session = [active, projection.pending].find(
    (candidate) =>
      candidate?.generation === target.generation &&
      candidate.sessionId === target.sessionId,
  );
  if (!session) {
    throw new Error('Browser installation authority advanced to a different generation.');
  }
  return {
    installationId: projection.installationId,
    generation: session.generation,
    sessionId: session.sessionId,
    status: session.status,
    stateVersion: projection.stateVersion,
    authorityPath: 'recovery_read',
    registeredAt: projection.lastRegisteredAt,
    occurredAt: projection.lastProviderEventAt,
  };
}

export function isBrowserInstallationPreparing(
  installation: VoiceBrowserInstallationEnsure,
): installation is VoiceBrowserInstallationPreparing {
  return installation.outcome === 'preparing';
}

export function browserInstallationCredentialTarget(
  installation: VoiceBrowserInstallationEnsure,
): BrowserRegistrationTarget | null {
  if (isBrowserInstallationPreparing(installation)) return null;
  const sessionId = installation.browserSession?.sessionId;
  if (!sessionId) return null;
  const projection = [
    { generation: installation.generation, sessionId: installation.sessionId },
    installation.pending,
  ].find((candidate) => candidate?.sessionId === sessionId);
  return projection
    ? {
        installationId: installation.installationId,
        generation: projection.generation,
        sessionId: projection.sessionId,
      }
    : null;
}

/**
 * Exact-generation event waiter. It caches an event that races ahead of waiter
 * creation and performs at most one authoritative read per named recovery
 * episode. It owns no SIP UA or React lifecycle state.
 */
export class BrowserRegistrationCoordinator {
  private readonly latest = new Map<string, RegistrationObservation>();
  private readonly waiters = new Map<string, Set<Waiter>>();
  private readonly recoveryReads = new Map<string, Promise<RegistrationObservation>>();
  private readonly timeoutMs: number;

  constructor(private readonly dependencies: CoordinatorDependencies) {
    this.timeoutMs = dependencies.timeoutMs ?? 30_000;
  }

  observeRealtime(
    payload: ContactCenterBrowserRegistrationChangedPayload,
  ): void {
    this.observe(observationFromRealtime(payload));
  }

  waitForRegistered(
    target: BrowserRegistrationTarget,
    recoveryEpisode: string,
  ): Promise<RegistrationObservation> {
    const cached = this.latest.get(targetKey(target));
    if (cached?.status === 'registered') return Promise.resolve(cached);
    if (cached && terminalStatuses.has(cached.status)) {
      return Promise.reject(this.terminalError(cached));
    }

    return new Promise<RegistrationObservation>((resolve, reject) => {
      const key = targetKey(target);
      const waiter: Waiter = {
        target,
        resolve,
        reject,
        timeout: window.setTimeout(() => {
          this.removeWaiter(key, waiter);
          void this.recoverOnce(recoveryEpisode, target).then(resolve, reject);
        }, this.timeoutMs),
      };
      const entries = this.waiters.get(key) ?? new Set<Waiter>();
      entries.add(waiter);
      this.waiters.set(key, entries);
    });
  }

  recoverOnce(
    episode: string,
    target: BrowserRegistrationTarget,
  ): Promise<RegistrationObservation> {
    const recoveryKey = JSON.stringify([
      episode,
      target.installationId,
      target.generation,
      target.sessionId,
    ]);
    const existing = this.recoveryReads.get(recoveryKey);
    if (existing) return existing;
    const recovery = this.dependencies.readInstallation(target.installationId)
      .then((projection) => {
        const observation = observationFromProjection(projection, target);
        this.observe(observation);
        if (observation.status === 'registered') return observation;
        if (terminalStatuses.has(observation.status)) throw this.terminalError(observation);
        throw new Error('Browser registration is not authoritative yet.');
      });
    this.recoveryReads.set(recoveryKey, recovery);
    return recovery;
  }

  dispose(): void {
    for (const entries of this.waiters.values()) {
      for (const waiter of entries) {
        window.clearTimeout(waiter.timeout);
        waiter.reject(new Error('Browser registration wait was cancelled.'));
      }
    }
    this.waiters.clear();
    this.latest.clear();
    this.recoveryReads.clear();
  }

  private observe(observation: RegistrationObservation): void {
    const key = targetKey(observation);
    const previous = this.latest.get(key);
    if (previous && observation.stateVersion <= previous.stateVersion) return;
    this.latest.set(key, observation);
    const entries = this.waiters.get(key);
    if (!entries) return;
    if (observation.status !== 'registered' && !terminalStatuses.has(observation.status)) return;
    this.waiters.delete(key);
    for (const waiter of entries) {
      window.clearTimeout(waiter.timeout);
      if (observation.status === 'registered') waiter.resolve(observation);
      else waiter.reject(this.terminalError(observation));
    }
  }

  private removeWaiter(key: string, waiter: Waiter): void {
    const entries = this.waiters.get(key);
    entries?.delete(waiter);
    if (entries?.size === 0) this.waiters.delete(key);
  }

  private terminalError(observation: RegistrationObservation): Error {
    return new Error(`Browser registration became ${observation.status}.`);
  }
}

const installationEnsureFlights = new Map<
  string,
  Promise<VoiceBrowserInstallationEnsure>
>();

export function ensureTabBrowserInstallation(
  storage: Storage = window.sessionStorage,
): Promise<VoiceBrowserInstallationEnsure> {
  const activationId = getBrowserActivationId(storage);
  const tabId = getBrowserTabId();
  // The device-instance id is intentionally resolved from its own per-profile
  // default store (localStorage) so each browser profile maps to one stable
  // installation. The reclaim token and tab fence above stay per-tab.
  const browserDeviceInstanceId = getBrowserDeviceInstanceId();
  const flightKey = JSON.stringify([
    browserDeviceInstanceId,
    activationId,
    tabId,
  ]);
  const existing = installationEnsureFlights.get(flightKey);
  if (existing) return existing;

  // React development remounts and ownership-epoch effects can overlap in the
  // same JS realm. Coalesce the exact activation request so only one provider
  // credential is minted and every surviving consumer receives that response.
  // A hard navigation has a new realm and is instead fenced by the server-side
  // preparation lease and request-abort compensation.
  let recoveryFlightKey: string | null = null;
  const flight = voiceApi
    .ensureBrowserInstallation({
      browserDeviceInstanceId,
      activationId,
      tabId,
    })
    .catch((error: unknown) => {
      if (
        !(error instanceof VoiceRequestRejectedError) ||
        error.code !== browserDeviceIdentityRetiredCode
      ) {
        throw error;
      }

      const replacementDeviceId = replaceRetiredBrowserDeviceInstanceId(
        browserDeviceInstanceId,
      );
      recoveryFlightKey = JSON.stringify([
        replacementDeviceId,
        activationId,
        tabId,
      ]);
      installationEnsureFlights.set(recoveryFlightKey, flight);
      return voiceApi.ensureBrowserInstallation({
        browserDeviceInstanceId: replacementDeviceId,
        activationId,
        tabId,
      });
    })
    .finally(() => {
      if (installationEnsureFlights.get(flightKey) === flight) {
        installationEnsureFlights.delete(flightKey);
      }
      if (
        recoveryFlightKey &&
        installationEnsureFlights.get(recoveryFlightKey) === flight
      ) {
        installationEnsureFlights.delete(recoveryFlightKey);
      }
    });
  installationEnsureFlights.set(flightKey, flight);
  return flight;
}


export type BrowserPreparationRetryAuthorization = {
  installationId: string;
  stateVersion: number;
  preparationGeneration: number;
};

export type BrowserPreparationPollOutcome =
  | BrowserPreparationRetryAuthorization & { action: 'retry_ensure' }
  | { action: 'terminal'; status: VoiceBrowserInstallationStatus };

type PreparationWakeWaiter = {
  minimumStateVersion: number;
  resolve: () => void;
  reject: (error: Error) => void;
};

type BrowserPreparationCoordinatorDependencies = {
  readInstallation: (installationId: string, signal?: AbortSignal) =>
    Promise<VoiceBrowserInstallationProjection>;
  random?: () => number;
};

const preparationTerminalStatuses = new Set<VoiceBrowserInstallationStatus>([
  'revoked',
  'expired',
  'failed',
]);
const preparationPollMinimumDelayMs = 250;
const preparationPollMaximumDelayMs = 10_000;
const preparationCancellationMessage =
  'Browser preparation wait was cancelled.';

function clampPreparationDelayMs(value: number): number {
  if (!Number.isFinite(value)) return preparationPollMinimumDelayMs;
  return Math.min(
    preparationPollMaximumDelayMs,
    Math.max(preparationPollMinimumDelayMs, Math.ceil(value)),
  );
}

/**
 * Projection-driven preparation wait. A 202 response never authorizes another
 * PUT; only an authoritative GET reporting retry_ensure at the same or newer
 * fenced state does. Polling is single-flight, jittered, delay-bounded, and stops as
 * soon as its AbortSignal is aborted.
 */
export class BrowserPreparationCoordinator {
  private readonly flights = new Map<
    string,
    Promise<BrowserPreparationPollOutcome>
  >();
  private readonly latestRealtimeStateVersion = new Map<string, number>();
  private readonly wakeWaiters = new Map<string, Set<PreparationWakeWaiter>>();

  constructor(
    private readonly dependencies: BrowserPreparationCoordinatorDependencies,
  ) {}

  observeRealtime(
    payload: ContactCenterBrowserRegistrationChangedPayload,
  ): void {
    const previous =
      this.latestRealtimeStateVersion.get(payload.installationId) ?? -1;
    if (payload.stateVersion <= previous) return;
    this.latestRealtimeStateVersion.set(payload.installationId, payload.stateVersion);
    const waiters = this.wakeWaiters.get(payload.installationId);
    if (!waiters) return;
    for (const waiter of [...waiters]) {
      if (payload.stateVersion < waiter.minimumStateVersion) continue;
      waiters.delete(waiter);
      waiter.resolve();
    }
    if (waiters.size === 0) this.wakeWaiters.delete(payload.installationId);
  }

  waitUntilRetryAuthorized(
    preparing: VoiceBrowserInstallationPreparing,
    signal: AbortSignal,
  ): Promise<BrowserPreparationPollOutcome> {
    const key = JSON.stringify([
      preparing.installationId,
      preparing.stateVersion,
      preparing.preparationGeneration,
    ]);
    const existing = this.flights.get(key);
    if (existing) return existing;

    const flight = this.poll(preparing, signal).finally(() => {
      if (this.flights.get(key) === flight) this.flights.delete(key);
    });
    this.flights.set(key, flight);
    return flight;
  }

  dispose(): void {
    const cancellation = new Error(preparationCancellationMessage);
    for (const waiters of this.wakeWaiters.values()) {
      for (const waiter of waiters) waiter.reject(cancellation);
    }
    this.wakeWaiters.clear();
    this.latestRealtimeStateVersion.clear();
    this.flights.clear();
  }

  private async poll(
    preparing: VoiceBrowserInstallationPreparing,
    signal: AbortSignal,
  ): Promise<BrowserPreparationPollOutcome> {
    const random = this.dependencies.random ?? Math.random;
    let delayMs = clampPreparationDelayMs(preparing.retryAfterMs);
    let minimumRealtimeStateVersion = preparing.stateVersion;

    for (;;) {
      if (signal.aborted) throw new Error(preparationCancellationMessage);

      await this.waitForPollOpportunity(
        preparing.installationId,
        minimumRealtimeStateVersion,
        delayMs,
        signal,
      );
      const observedRealtimeStateVersion =
        this.latestRealtimeStateVersion.get(preparing.installationId) ?? -1;
      minimumRealtimeStateVersion = Math.max(
        minimumRealtimeStateVersion,
        observedRealtimeStateVersion + 1,
      );
      let projection: VoiceBrowserInstallationProjection | undefined;
      try {
        projection = await this.dependencies.readInstallation(
          preparing.installationId,
          signal,
        );
      } catch (error) {
        if (signal.aborted) throw new Error(preparationCancellationMessage);
        if (
          error instanceof VoiceRequestRejectedError &&
          error.status !== 429 &&
          error.status < 500
        ) {
          throw error;
        }
        if (
          !(error instanceof VoiceRequestRejectedError) &&
          !(error instanceof TypeError)
        ) {
          throw error;
        }
      }
      if (!projection) {
        delayMs = this.nextDelay(delayMs, random);
        continue;
      }
      if (signal.aborted) throw new Error(preparationCancellationMessage);

      if (
        projection.installationId !== preparing.installationId ||
        projection.stateVersion < preparing.stateVersion ||
        projection.preparationGeneration < preparing.preparationGeneration
      ) {
        // A stale GET can never downgrade the 202 fence or authorize a PUT.
        delayMs = this.nextDelay(delayMs, random);
        continue;
      }

      const disposition = projection.ensureDisposition ?? null;
      if (disposition?.nextAction === 'retry_ensure') {
        return {
          action: 'retry_ensure',
          installationId: projection.installationId,
          stateVersion: projection.stateVersion,
          preparationGeneration: projection.preparationGeneration,
        };
      }
      if (preparationTerminalStatuses.has(projection.status)) {
        return { action: 'terminal', status: projection.status };
      }
      if (disposition?.nextAction === 'none') {
        throw new Error(
          'Browser phone preparation is no longer active for this tab.',
        );
      }

      delayMs = this.nextDelay(delayMs, random);
    }
  }

  private waitForPollOpportunity(
    installationId: string,
    minimumStateVersion: number,
    delayMs: number,
    signal: AbortSignal,
  ): Promise<void> {
    if (
      (this.latestRealtimeStateVersion.get(installationId) ?? -1) >=
      minimumStateVersion
    ) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new Error(preparationCancellationMessage));
        return;
      }
      const waiter: PreparationWakeWaiter = {
        minimumStateVersion,
        resolve: () => finish(resolve),
        reject: (error) => finish(() => reject(error)),
      };
      const waiters = this.wakeWaiters.get(installationId) ?? new Set();
      waiters.add(waiter);
      this.wakeWaiters.set(installationId, waiters);
      const timeout = window.setTimeout(() => finish(resolve), delayMs);
      const onAbort = () =>
        finish(() => reject(new Error(preparationCancellationMessage)));
      const finish = (complete: () => void) => {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', onAbort);
        const current = this.wakeWaiters.get(installationId);
        current?.delete(waiter);
        if (current?.size === 0) this.wakeWaiters.delete(installationId);
        complete();
      };
      signal.addEventListener('abort', onAbort, { once: true });
      if (
        (this.latestRealtimeStateVersion.get(installationId) ?? -1) >=
        minimumStateVersion
      ) {
        waiter.resolve();
      }
    });
  }

  private nextDelay(currentDelayMs: number, random: () => number): number {
    const backoff = currentDelayMs * 1.5;
    const jitter = 0.75 + random() * 0.5;
    return clampPreparationDelayMs(backoff * jitter);
  }
}
